import React, { useState, useEffect, useMemo } from 'react';
import SelkirkIcon from './icons/SelkirkIcon';
import { ICON } from '../icons/iconIds';
import IconText from './icons/IconText';
import Papa from 'papaparse';
import { v4 as uuidv4 } from 'uuid';
import Layout from './layout/Layout';
import Card from './ui/Card';
import Button from './ui/Button';
import Spinner from './ui/Spinner';
import IntervalStrip from './IntervalStrip';
import HoleLocationModal from './HoleLocationModal';
import { assayCompositingService } from '../services/assayCompositingService';
import { sampleTrackingService } from '../services/sampleTrackingService';
import { drillingService } from '../services/drillingService';
import { 
  compositeInterval, 
  defaultEconomicConfig,
  defaultHeaderMapping,
  rockColorMap
} from '../utils/composite';
import './AssayCompositing.css';

const AssayCompositing = ({ user }) => {
  // Data state
  const [assayData, setAssayData] = useState([]);
  const [availableHoles, setAvailableHoles] = useState([]); // NEW: hole ID list from database
  const [loadedHolesCache, setLoadedHolesCache] = useState({}); // NEW: {holeId: data[]}
  const [blockModelCache, setBlockModelCache] = useState({}); // {holeId: blockModelData[]}
  const [loadingBlockModel, setLoadingBlockModel] = useState(false);
  const [dataSource, setDataSource] = useState('database'); // 'database' | 'csv'
  const [loading, setLoading] = useState(true);
  const [loadingHole, setLoadingHole] = useState(false); // NEW: loading state for individual hole
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Loading data from database...');

  // Hole metadata for filtering and details
  const [holeMetadata, setHoleMetadata] = useState(new Map()); // hole_id -> QAQC/status/news release
  const [drillingDetails, setDrillingDetails] = useState(new Map()); // hole_id -> drilling master details
  const [statusFilter, setStatusFilter] = useState('all');
  const [qaqcFilter, setQaqcFilter] = useState('all');
  const [newsReleaseFilter, setNewsReleaseFilter] = useState('not_released');

  // CSV upload state
  const [rawCSVRows, setRawCSVRows] = useState([]);
  const [csvHeaders, setCSVHeaders] = useState([]);
  const [headerMapping, setHeaderMapping] = useState(defaultHeaderMapping);
  const [showMapping, setShowMapping] = useState(false);

  // Filtering and selection state
  const [selectedHole, setSelectedHole] = useState('');
  const [fromDepth, setFromDepth] = useState(0);
  const [toDepth, setToDepth] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showMapModal, setShowMapModal] = useState(false);
  
  // Visual controls
  const [pxPerMeter, setPxPerMeter] = useState(4);
  const [barScale, setBarScale] = useState(1);
  const [viewMode, setViewMode] = useState('cueq'); // 'cueq' | 'individual' | 'blockModel'
  
  // Economic configuration
  const [config, setConfig] = useState(defaultEconomicConfig);
  const [showEconomicConfig, setShowEconomicConfig] = useState(false);
  
  // Composite accumulation
  const [compositeRows, setCompositeRows] = useState([]);
  const [tableSelection, setTableSelection] = useState(new Set());

  // Load data on component mount
  useEffect(() => {
    loadDatabaseData();
  }, []);

  const loadDatabaseData = async () => {
    try {
      setLoading(true);
      setError(null);
      setStatus('Loading hole list from database...');
      
      const data = await assayCompositingService.getDashboardData();
      
      setAvailableHoles(data.holeIds || []);
      setDataSource('database');
      setStatus(`Loaded ${data.holeIds?.length || 0} holes from database`);
      
      // Load metadata in parallel
      await Promise.all([loadHoleMetadata(), loadDrillingDetails()]);
    } catch (err) {
      console.error('❌ Error loading hole list:', err);
      setError(err.message || 'Failed to load hole list from database');
      setStatus('Failed to load data from database. Try uploading a CSV file.');
    } finally {
      setLoading(false);
    }
  };

  const loadHoleMetadata = async () => {
    try {
      const result = await sampleTrackingService.getDashboardData();
      const metadataMap = new Map();
      
      result.data.forEach(hole => {
        metadataMap.set(hole.hole_id, {
          workorder_statuses_array: hole.workorder_statuses_array || [],
          qaqc_passed_array: hole.qaqc_passed_array || [],
          news_release_date: hole.news_release_date,
          // Basic collar data from sample tracking
          easting: hole.easting,
          northing: hole.northing,
          azi: hole.azi,
          dip: hole.dip,
          eoh: hole.eoh
        });
      });
      
      setHoleMetadata(metadataMap);
      console.log(`✅ Loaded metadata for ${metadataMap.size} holes`);
    } catch (err) {
      console.warn('⚠️ Failed to load hole metadata for filtering:', err);
    }
  };

  const loadDrillingDetails = async () => {
    try {
      // Fetch all drilling data (includes target_lens, drill_rig, pad, comments, etc.)
      const result = await drillingService.getAllDrilling({ limit: 500 });
      const detailsMap = new Map();
      
      result.data.forEach(hole => {
        detailsMap.set(hole.hole_id, {
          prospect: hole.prospect,
          drill_rig: hole.drill_rig,
          pad_id: hole.pad_id,
          master_target_lens: hole.master_target_lens,
          master_target_lens_m: hole.master_target_lens_m,
          master_azimuth: hole.master_azimuth,
          master_dip: hole.master_dip,
          master_comments: hole.master_comments,
          master_phase: hole.master_phase,
          status: hole.status,
          // Coordinates for map
          latitude: hole.latitude,
          longitude: hole.longitude
        });
      });
      
      setDrillingDetails(detailsMap);
      console.log(`✅ Loaded drilling details for ${detailsMap.size} holes`);
    } catch (err) {
      console.warn('⚠️ Failed to load drilling details:', err);
    }
  };

  const loadHoleData = async (holeId) => {
    // Check cache first
    if (loadedHolesCache[holeId]) {
      setAssayData(loadedHolesCache[holeId]);
      setStatus(`Using cached data for ${holeId}`);
      return;
    }
    
    try {
      setLoadingHole(true);
      setStatus(`Loading data for ${holeId}...`);
      
      const holeData = await assayCompositingService.getHoleData(holeId);
      
      // Cache the data
      setLoadedHolesCache(prev => ({ ...prev, [holeId]: holeData }));
      setAssayData(holeData);
      setStatus(`Loaded ${holeData.length} samples for ${holeId}`);
    } catch (err) {
      console.error(`❌ Error loading data for ${holeId}:`, err);
      setError(`Failed to load data for ${holeId}: ${err.message}`);
    } finally {
      setLoadingHole(false);
    }
  };

  const loadBlockModelData = async (holeId) => {
    // Check cache first
    if (blockModelCache[holeId]) {
      console.log(`✅ Using cached block model data for ${holeId}`);
      return;
    }

    try {
      setLoadingBlockModel(true);
      console.log(`📡 Loading block model data for ${holeId}...`);
      
      const blockData = await assayCompositingService.getBlockModelData(holeId);
      
      setBlockModelCache(prev => ({
        ...prev,
        [holeId]: blockData
      }));
      
      console.log(`✅ Loaded ${blockData.length} block model records for ${holeId}`);
    } catch (err) {
      console.error(`❌ Failed to load block model data for ${holeId}:`, err);
    } finally {
      setLoadingBlockModel(false);
    }
  };

  const handleCSVUpload = async (file) => {
    try {
      setLoading(true);
      setStatus('Parsing CSV file...');
      
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: false,
        complete: (results) => {
          const headers = results.meta.fields || [];
          const rawRows = (results.data || []).filter(r => r && Object.keys(r).length > 0);
          
          setCSVHeaders(headers);
          setRawCSVRows(rawRows);
          
          // Try to apply mapping
          const mapped = assayCompositingService.applyHeaderMapping(rawRows, headerMapping);
          setAssayData(mapped);
          setDataSource('csv');
          setStatus(`Loaded ${mapped.length} rows from CSV (${headers.length} columns)`);
          setLoading(false);
        },
        error: (err) => {
          setError(`CSV parsing failed: ${err.message}`);
          setStatus('Failed to parse CSV');
          setLoading(false);
        }
      });
    } catch (err) {
      console.error('❌ Error uploading CSV:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleReapplyMapping = () => {
    if (!rawCSVRows.length) return;
    const mapped = assayCompositingService.applyHeaderMapping(rawCSVRows, headerMapping);
    setAssayData(mapped);
    setStatus(`Remapped ${mapped.length} rows`);
  };

  const handleMappingChange = (field, value) => {
    setHeaderMapping(prev => ({ ...prev, [field]: value }));
  };

  // Get unique holes with filters applied
  const holes = useMemo(() => {
    let holeList = dataSource === 'database' 
      ? availableHoles 
      : assayCompositingService.getUniqueHoles(assayData);
    
    // Apply filters only in database mode
    if (dataSource === 'database' && holeMetadata.size > 0) {
      // Status filter
      if (statusFilter !== 'all') {
        holeList = holeList.filter(holeId => {
          const meta = holeMetadata.get(holeId);
          if (!meta || !meta.workorder_statuses_array?.length) return true;
          return meta.workorder_statuses_array.some(s => 
            s?.toLowerCase() === statusFilter.toLowerCase()
          );
        });
      }
      
      // QAQC filter
      if (qaqcFilter !== 'all') {
        holeList = holeList.filter(holeId => {
          const meta = holeMetadata.get(holeId);
          if (!meta || !meta.qaqc_passed_array?.length) return true;
          if (qaqcFilter === 'passed') {
            return meta.qaqc_passed_array.some(q => q === 't');
          } else if (qaqcFilter === 'failed') {
            return meta.qaqc_passed_array.some(q => q === 'f');
          }
          return true;
        });
      }
      
      // News release filter
      if (newsReleaseFilter !== 'all') {
        holeList = holeList.filter(holeId => {
          const meta = holeMetadata.get(holeId);
          if (newsReleaseFilter === 'not_released') {
            return !meta?.news_release_date || 
                   meta.news_release_date === '' || 
                   meta.news_release_date === 'null';
          } else if (newsReleaseFilter === 'released') {
            return meta?.news_release_date && 
                   meta.news_release_date !== '' && 
                   meta.news_release_date !== 'null';
          } else {
            return meta?.news_release_date === newsReleaseFilter;
          }
        });
      }
    }
    
    // Don't return holes until metadata loads if filters are active
    // This prevents loading the wrong hole before filters can be applied
    if (dataSource === 'database' && holeMetadata.size === 0) {
      const filtersActive = statusFilter !== 'all' || 
                           qaqcFilter !== 'all' || 
                           newsReleaseFilter !== 'all';
      if (filtersActive) {
        return []; // Wait for metadata before selecting any hole
      }
    }
    
    return holeList;
  }, [dataSource, availableHoles, assayData, holeMetadata, statusFilter, qaqcFilter, newsReleaseFilter]);

  // Get data for selected hole
  const holeRows = useMemo(
    () => assayData.filter(r => r.hole_id === selectedHole),
    [assayData, selectedHole]
  );

  // Merge block model data with assay data for display
  const holeRowsWithBlockModel = useMemo(() => {
    if (viewMode !== 'blockModel' || !blockModelCache[selectedHole]) {
      return holeRows;
    }
    
    const blockData = blockModelCache[selectedHole];
    const normKey = (v) => String(v ?? '').trim().toUpperCase();
    const blockMap = new Map(blockData.map(b => [normKey(b.sample_number), b]));
    
    const merged = holeRows.map(row => {
      const block = blockMap.get(normKey(row.sample_number));
      if (!block || !block.has_block_match) {
        return {
          ...row,
          hasBlockData: false,
          block_match_status: block?.match_status ?? 'NOT IN BACKFLAG'
        };
      }
      
      return {
        ...row,
        hasBlockData: true,
        block_match_status: block.match_status,
        cu_block: block.cu_block,
        au_block: block.au_block,
        ag_block: block.ag_block,
        recov_cu: block.recov_cu,
        recov_au: block.recov_au,
        recov_ag: block.recov_ag,
        block_zone: block.zone,
        block_class: block.class,
        // Use block model grades for display in block model view
        cu_display: block.cu_block,
        au_display: block.au_block,
        ag_display: block.ag_block
      };
    });
    
    console.log(
      `🧱 Block model match for ${selectedHole}: ${merged.filter(r => r.hasBlockData).length}/${merged.length} samples`
    );
    return merged;
  }, [holeRows, viewMode, selectedHole, blockModelCache]);

  // Filter rows based on selection
  const filteredRows = useMemo(() => {
    const rows = viewMode === 'blockModel' ? holeRowsWithBlockModel : holeRows;
    
    if (selectedIds.size) {
      return rows.filter(r => selectedIds.has(r.sample_number));
    }
    return rows.filter(r => r.from_depth >= fromDepth && r.to_depth <= toDepth);
  }, [holeRows, holeRowsWithBlockModel, fromDepth, toDepth, selectedIds, viewMode]);

  // Compute composite
  const composite = useMemo(() => {
    if (!filteredRows.length) return null;
    
    // For block model view, use block model grades if available
    if (viewMode === 'blockModel') {
      const rowsWithBlockData = filteredRows.filter(r => r.hasBlockData);
      if (rowsWithBlockData.length === 0) return null;
      
      // Use block model grades for calculation
      const modifiedRows = rowsWithBlockData.map(r => ({
        ...r,
        cu_best: r.cu_block,
        au_best: r.au_block,
        ag_best: r.ag_block
      }));
      
      return compositeInterval(modifiedRows, config);
    }
    
    return compositeInterval(filteredRows, config);
  }, [filteredRows, config, viewMode]);

  // Get depth range for selected hole
  const range = useMemo(
    () => assayCompositingService.getHoleDepthRange(assayData, selectedHole),
    [assayData, selectedHole]
  );

  // Rock colors for visualization
  const rockColors = useMemo(() => rockColorMap(holeRows), [holeRows]);

  // Prepare map data for selected hole
  const selectedHoleMapData = useMemo(() => {
    if (!selectedHole || dataSource !== 'database') return null;
    
    const details = drillingDetails.get(selectedHole);
    const metadata = holeMetadata.get(selectedHole);
    
    if (!details || !metadata) return null;
    
    // DrillHoleMap expects latitude/longitude
    // drilling_data API provides these from ST_Transform(geom, 4326)
    return {
      hole_id: selectedHole,
      latitude: details.latitude,
      longitude: details.longitude,
      prospect: details.prospect,
      drill_rig: details.drill_rig,
      hole_status: details.status,
      easting: metadata.easting,
      northing: metadata.northing
    };
  }, [selectedHole, drillingDetails, holeMetadata, dataSource]);

  // All holes for map context
  const allHolesMapData = useMemo(() => {
    if (dataSource !== 'database') return [];
    
    return Array.from(drillingDetails.entries())
      .map(([holeId, details]) => {
        const metadata = holeMetadata.get(holeId);
        return {
          hole_id: holeId,
          latitude: details.latitude,
          longitude: details.longitude,
          prospect: details.prospect,
          drill_rig: details.drill_rig,
          hole_status: details.status,
          easting: metadata?.easting,
          northing: metadata?.northing
        };
      })
      .filter(h => h.latitude && h.longitude);
  }, [drillingDetails, holeMetadata, dataSource]);

  // Auto-select first hole and set depth range
  useEffect(() => {
    if (!holes.length) return;
    
    if (!selectedHole || !holes.includes(selectedHole)) {
      const firstHole = holes[0];
      setSelectedHole(firstHole);
      
      // Load hole data for database mode
      if (dataSource === 'database' && firstHole) {
        loadHoleData(firstHole);
      }
    } else {
      // Load data when hole changes in database mode
      if (dataSource === 'database') {
        loadHoleData(selectedHole);
      } else {
        // For CSV mode, just update depth range
        const holeRange = assayCompositingService.getHoleDepthRange(assayData, selectedHole);
        if (holeRange) {
          setFromDepth(holeRange.minFrom);
          setToDepth(holeRange.maxTo);
        }
      }
    }
  }, [holes, selectedHole, dataSource]);

  // Load block model data when switching to block model view
  useEffect(() => {
    if (viewMode === 'blockModel' && selectedHole && dataSource === 'database') {
      loadBlockModelData(selectedHole);
    }
  }, [viewMode, selectedHole, dataSource]);

  // Update depth range when assay data changes (after hole is loaded)
  useEffect(() => {
    if (!assayData.length || !selectedHole) return;
    
    const holeRange = assayCompositingService.getHoleDepthRange(assayData, selectedHole);
    if (holeRange) {
      setFromDepth(holeRange.minFrom);
      setToDepth(holeRange.maxTo);
    }
  }, [assayData, selectedHole]);

  const recomputeDepthBounds = (rows) => {
    if (!rows.length) return null;
    const min = Math.min(...rows.map(r => r.from_depth));
    const max = Math.max(...rows.map(r => r.to_depth));
    return { min, max };
  };

  const handleSelectionChange = (from, to, options = {}) => {
    if (options.toggleSampleId) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(options.toggleSampleId)) {
          next.delete(options.toggleSampleId);
        } else {
          next.add(options.toggleSampleId);
        }
        const selectedRows = holeRows.filter(r => next.has(r.sample_number));
        const bounds = recomputeDepthBounds(selectedRows);
        if (bounds) {
          setFromDepth(bounds.min);
          setToDepth(bounds.max);
        }
        return next;
      });
      return;
    }

    if (options.rangeIds) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        const allSelected = options.rangeIds.every(id => next.has(id));
        if (allSelected) {
          options.rangeIds.forEach(id => next.delete(id));
        } else {
          options.rangeIds.forEach(id => next.add(id));
        }
        const selectedRows = holeRows.filter(r => next.has(r.sample_number));
        const bounds = recomputeDepthBounds(selectedRows);
        if (bounds) {
          setFromDepth(bounds.min);
          setToDepth(bounds.max);
        }
        return next;
      });
      return;
    }

    // Default: replace range selection
    setSelectedIds(new Set());
    setFromDepth(from);
    setToDepth(to);
  };

  const addCompositeRow = (incl) => {
    if (!composite || !holeRows.length) return;
    
    // Get unique lithologies from filtered rows
    const lithologies = Array.from(new Set(filteredRows.map(r => r.rock_type)))
      .filter(rt => rt.trim() !== '')
      .sort()
      .join(', ');
    
    const row = {
      id: uuidv4(),
      hole_id: selectedHole,
      from: composite.fromDepthMin ?? fromDepth,
      to: composite.toDepthMax ?? toDepth,
      length: composite.totalLength,
      cu: composite.avgCu,
      au: composite.avgAu,
      ag: composite.avgAg,
      cuEq: composite.cuEqPct,
      incl,
      lithology: lithologies
    };
    setCompositeRows(prev => [...prev, row]);
  };

  const removeSelectedCompositeRows = () => {
    if (!tableSelection.size) return;
    setCompositeRows(prev => prev.filter(r => !tableSelection.has(r.id)));
    setTableSelection(new Set());
  };

  const exportCompositeRows = () => {
    if (!compositeRows.length) return;
    const sorted = [...compositeRows].sort(
      (a, b) =>
        a.hole_id.localeCompare(b.hole_id) ||
        a.from - b.from ||
        a.to - b.to
    );
    const rows = sorted.map(r => ({
      hole_id: r.hole_id,
      incl: r.incl ? 'Incl.' : '',
      from_m: r.from,
      to_m: r.to,
      length_m: r.length,
      cu_pct: r.cu,
      au_gt: r.au,
      ag_gt: r.ag,
      cueq_pct: r.cuEq,
      lithology: r.lithology
    }));
    assayCompositingService.exportToCSV('composites.csv', rows);
  };

  const exportFilteredRows = () => {
    if (!filteredRows.length) return;
    assayCompositingService.exportToCSV('filtered_rows.csv', filteredRows);
  };

  const updateConfig = (section, key, value) => {
    setConfig(prev => ({
      ...prev,
      [section]: { ...prev[section], [key]: value }
    }));
  };

  const numberInput = (val, decimals = 3) =>
    Number.isFinite(val) ? val.toFixed(decimals) : '';

  return (
    <Layout user={user} currentRoute="/assay-compositing">
      <div className="assay-compositing">
        {/* Header */}
        <div className="app-header compact">
          <div>
            <IconText icon={ICON.appAssayCompositing} as="h1">Assay Compositer</IconText>
            <p className="status-text">
              <span className={`source-badge ${dataSource === 'database' ? 'db' : 'csv'}`}>
                {dataSource === 'database' ? (<IconText icon={ICON.uiDatabase} iconSize={14}>Database</IconText>) : (<IconText icon={ICON.uiDocument} iconSize={14}>CSV</IconText>)}
              </span>
              {status}
            </p>
          </div>
          <div className="file-actions">
            <label className="button secondary">
              <IconText icon={ICON.uiFolderOpen} iconSize={16}>Load CSV (Ad-hoc)</IconText>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleCSVUpload(file);
                }}
                style={{ display: 'none' }}
              />
            </label>
            {dataSource === 'csv' && rawCSVRows.length > 0 && (
              <Button onClick={handleReapplyMapping} variant="ghost">
                <IconText icon={ICON.uiRefresh} iconSize={16}>Reapply Mapping</IconText>
              </Button>
            )}
            {dataSource === 'csv' && (
              <Button onClick={loadDatabaseData} variant="ghost">
                <IconText icon={ICON.uiDatabase} iconSize={16}>Load from Database</IconText>
              </Button>
            )}
          </div>
        </div>

        {loading && (
          <div className="loading-container">
            <Spinner size="large" />
            <p>{status}</p>
          </div>
        )}

        {error && (
          <Card className="error-card">
            <IconText icon={ICON.uiError} as="h3">Error</IconText>
            <p>{error}</p>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <Button onClick={loadDatabaseData}>Retry Database</Button>
              <label className="button secondary">
                Load CSV Instead
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleCSVUpload(file);
                  }}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
          </Card>
        )}

        {!loading && !error && (dataSource === 'database' ? availableHoles.length > 0 : assayData.length > 0) && (
          <>
            {/* Filter Controls - only show in database mode */}
            {dataSource === 'database' && availableHoles.length > 0 && (
              <div className="filter-controls">
                <div className="filter-group">
                  <label>Status:</label>
                  <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">All Statuses</option>
                    <option value="Active">Active</option>
                    <option value="Finalized">Finalized</option>
                    <option value="Hold">Hold</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>QAQC:</label>
                  <select value={qaqcFilter} onChange={(e) => setQaqcFilter(e.target.value)}>
                    <option value="all">All</option>
                    <option value="passed">Passed</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label>News Release:</label>
                  <select value={newsReleaseFilter} onChange={(e) => setNewsReleaseFilter(e.target.value)}>
                    <option value="all">All Holes</option>
                    <option value="not_released">Not Released</option>
                    <option value="released">Released</option>
                  </select>
                </div>
                
                <div className="filter-status">
                  Showing {holes.length} of {availableHoles.length} holes
                </div>
              </div>
            )}

            {/* Hole Details Panel - show when hole selected in database mode */}
            {dataSource === 'database' && selectedHole && drillingDetails.size > 0 && (
              <div className="hole-details-panel">
                <div className="hole-details-header">
                  <h4>{selectedHole}</h4>
                  {drillingDetails.get(selectedHole)?.status && (
                    <span className="hole-status-badge">
                      {drillingDetails.get(selectedHole).status}
                    </span>
                  )}
                  {selectedHoleMapData?.latitude && selectedHoleMapData?.longitude && (
                    <button
                      className="map-button"
                      onClick={() => setShowMapModal(true)}
                      title="Show hole location on map"
                    >
                      <SelkirkIcon name={ICON.uiCameraMap} size={16} decorative />
                    </button>
                  )}
                </div>
                
                <div className="hole-details-badges">
                  {drillingDetails.get(selectedHole)?.prospect && (
                    <span className="detail-badge prospect-badge">
                      <><SelkirkIcon name={ICON.drillFilterZone} size={14} decorative /> {drillingDetails.get(selectedHole).prospect}</>
                    </span>
                  )}
                  {drillingDetails.get(selectedHole)?.drill_rig && (
                    <span className="detail-badge rig-badge">
                      <><SelkirkIcon name={ICON.drillFilterRig} size={14} decorative /> {drillingDetails.get(selectedHole).drill_rig}</>
                    </span>
                  )}
                  {drillingDetails.get(selectedHole)?.pad_id && (
                    <span className="detail-badge pad-badge">
                      <><SelkirkIcon name={ICON.uiLocationPin} size={14} decorative /> {drillingDetails.get(selectedHole).pad_id}</>
                    </span>
                  )}
                </div>
                
                {(drillingDetails.get(selectedHole)?.master_target_lens || 
                  drillingDetails.get(selectedHole)?.master_azimuth ||
                  holeMetadata.get(selectedHole)?.azi ||
                  drillingDetails.get(selectedHole)?.master_comments) && (
                  <div className="hole-details-section">
                    <h5>Hole setup (master)</h5>
                    <div className="hole-details-grid">
                      {drillingDetails.get(selectedHole)?.master_target_lens && (
                        <div className="detail-row">
                          <span className="detail-label">Target lens:</span>
                          <span className="detail-value">{drillingDetails.get(selectedHole).master_target_lens}</span>
                        </div>
                      )}
                      {drillingDetails.get(selectedHole)?.master_target_lens_m && (
                        <div className="detail-row">
                          <span className="detail-label">Target lens (m):</span>
                          <span className="detail-value">{drillingDetails.get(selectedHole).master_target_lens_m}</span>
                        </div>
                      )}
                      {(drillingDetails.get(selectedHole)?.master_azimuth || holeMetadata.get(selectedHole)?.azi) && (
                        <div className="detail-row">
                          <span className="detail-label">Azimuth:</span>
                          <span className="detail-value">
                            {drillingDetails.get(selectedHole)?.master_azimuth || holeMetadata.get(selectedHole)?.azi}
                          </span>
                        </div>
                      )}
                      {(drillingDetails.get(selectedHole)?.master_dip || holeMetadata.get(selectedHole)?.dip) && (
                        <div className="detail-row">
                          <span className="detail-label">Dip:</span>
                          <span className="detail-value">
                            {drillingDetails.get(selectedHole)?.master_dip || holeMetadata.get(selectedHole)?.dip}
                          </span>
                        </div>
                      )}
                      {drillingDetails.get(selectedHole)?.master_comments && (
                        <div className="detail-row detail-row-full">
                          <span className="detail-label">Comments:</span>
                          <span className="detail-value">{drillingDetails.get(selectedHole).master_comments}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Composite Result Panel (Sticky) */}
            <div className="composite-panel sticky">
              <div className="composite-header">
                <IconText icon={ICON.uiChartAnalytics} as="h3">Composite Result</IconText>
                <div className="controls-row">
                  <div className="view-mode-toggle">
                    <button 
                      className={`toggle-btn ${viewMode === 'cueq' ? 'active' : ''}`}
                      onClick={() => setViewMode('cueq')}
                      title="CuEq stacked bar view"
                    >
                      CuEq
                    </button>
                    <button 
                      className={`toggle-btn ${viewMode === 'individual' ? 'active' : ''}`}
                      onClick={() => setViewMode('individual')}
                      title="Individual metal bars"
                    >
                      Cu/Au/Ag
                    </button>
                    <button 
                      className={`toggle-btn ${viewMode === 'blockModel' ? 'active' : ''}`}
                      onClick={() => setViewMode('blockModel')}
                      title="Block model expected grades"
                      disabled={dataSource === 'csv'}
                    >
                      Block Model Expected
                    </button>
                    {viewMode === 'blockModel' && loadingBlockModel && (
                      <Spinner size="small" className="block-model-loading-spinner" />
                    )}
                  </div>
                  <label className="control-inline">
                    Zoom
                    <input
                      type="range"
                      min={1}
                      max={40}
                      step={0.5}
                      value={pxPerMeter}
                      onChange={(e) => setPxPerMeter(parseFloat(e.target.value) || 1)}
                    />
                    <span className="control-value">{pxPerMeter.toFixed(1)} px/m</span>
                  </label>
                  <label className="control-inline">
                    Bar Width
                    <input
                      type="range"
                      min={0.2}
                      max={5}
                      step={0.1}
                      value={barScale}
                      onChange={(e) => setBarScale(parseFloat(e.target.value) || 1)}
                    />
                    <span className="control-value">{barScale.toFixed(1)}x</span>
                  </label>
                  {selectedIds.size > 0 && (
                    <Button 
                      size="small"
                      variant="ghost"
                      onClick={() => setSelectedIds(new Set())}
                    >
                      Deselect All ({selectedIds.size})
                    </Button>
                  )}
                </div>
              </div>
              {!composite ? (
                <p className="hint">Select a hole and interval to view results.</p>
              ) : (
                <>
                  <div className="result-grid horizontal">
                    <ResultItem label="Samples" value={composite.rowsCount} />
                    <ResultItem label="Length (m)" value={composite.totalLength.toFixed(2)} />
                    <ResultItem label="Cu avg (%)" value={composite.avgCu.toFixed(3)} />
                    <ResultItem label="Au avg (g/t)" value={composite.avgAu.toFixed(3)} />
                    <ResultItem label="Ag avg (g/t)" value={composite.avgAg.toFixed(2)} />
                    <ResultItem label="Cu pct*m" value={composite.pctMeters.toFixed(2)} />
                    <ResultItem label="From depth" value={composite.fromDepthMin?.toFixed(2)} />
                    <ResultItem label="To depth" value={composite.toDepthMax?.toFixed(2)} />
                    <ResultItem label="Cu value $/t" value={composite.valueCuUsdPerT.toFixed(2)} />
                    <ResultItem label="Au value $/t" value={composite.valueAuUsdPerT.toFixed(2)} />
                    <ResultItem label="Ag value $/t" value={composite.valueAgUsdPerT.toFixed(2)} />
                    <ResultItem label="NSR $/t" value={composite.nsrUsdPerT.toFixed(2)} />
                    <ResultItem label="CuEq (%)" value={composite.cuEqPct.toFixed(3)} isCuEq />
                  </div>
                  <div className="actions-row">
                    <Button onClick={() => addCompositeRow(false)}>
                      <IconText icon={ICON.uiAdd} iconSize={16}>Add to table</IconText>
                    </Button>
                    <Button variant="ghost" onClick={() => addCompositeRow(true)}>
                      <IconText icon={ICON.uiAdd} iconSize={16}>Add as Incl.</IconText>
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Main Grid Layout */}
            <div className="main-grid">
              {/* Left Column */}
              <div className="left-column">
                {/* Header Mapping (CSV only) */}
                {dataSource === 'csv' && (
                  <div className="panel collapsible">
                    <div className="panel-header row">
                      <IconText icon={ICON.uiCameraMap} as="h3">Header Mapping</IconText>
                      <Button
                        size="small"
                        variant="ghost"
                        onClick={() => setShowMapping(v => !v)}
                      >
                        {showMapping ? 'Hide' : 'Show'}
                      </Button>
                    </div>
                    {showMapping && csvHeaders.length > 0 && (
                      <div className="mapping-grid">
                        {Object.keys(defaultHeaderMapping).map(key => (
                          <label key={key} className="mapping-row">
                            <span>{key}</span>
                            <select
                              value={headerMapping[key]}
                              onChange={(e) => handleMappingChange(key, e.target.value)}
                            >
                              {csvHeaders.map(h => (
                                <option key={h} value={h}>{h}</option>
                              ))}
                            </select>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Filter & Interval */}
                <div className="panel">
                  <IconText icon={ICON.uiFilter} as="h3">Filter & Interval</IconText>
                  {!holes.length ? (
                    <p className="hint">Holes will appear after loading data.</p>
                  ) : (
                    <>
                      <label className="stacked">
                        Hole
                        <select
                          value={selectedHole}
                          onChange={(e) => setSelectedHole(e.target.value)}
                        >
                          {holes.map(h => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                      </label>
                      <div className="range-grid">
                        <label className="stacked">
                          From (m)
                          <input
                            type="number"
                            step="0.01"
                            value={numberInput(fromDepth)}
                            onChange={(e) => setFromDepth(parseFloat(e.target.value) || 0)}
                          />
                        </label>
                        <label className="stacked">
                          To (m)
                          <input
                            type="number"
                            step="0.01"
                            value={numberInput(toDepth)}
                            onChange={(e) => setToDepth(parseFloat(e.target.value) || 0)}
                          />
                        </label>
                      </div>
                      <div className="pill">{filteredRows.length} samples selected</div>
                      <div className="actions-row">
                        <Button onClick={exportFilteredRows} disabled={!filteredRows.length}>
                          <IconText icon={ICON.uiExport} iconSize={16}>Export filtered rows</IconText>
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                {/* Rock Type Legend */}
                {Object.keys(rockColors).length > 0 && (
                  <div className="panel">
                    <IconText icon={ICON.uiTip} as="h3">Rock Type Legend</IconText>
                    <div className="legend vertical">
                      {Object.entries(rockColors).map(([rock, color]) => (
                        <div className="legend-item" key={rock}>
                          <span className="legend-swatch" style={{ background: color }} />
                          <span className="legend-label">{rock}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Economic Inputs */}
                <div className="panel collapsible">
                  <div className="panel-header row">
                    <IconText icon={ICON.uiCalculator} as="h3">Economic Inputs</IconText>
                    <Button
                      size="small"
                      variant="ghost"
                      onClick={() => setShowEconomicConfig(v => !v)}
                    >
                      {showEconomicConfig ? 'Hide' : 'Show'}
                    </Button>
                  </div>
                  {showEconomicConfig && (
                    <div className="config-grid">
                      <h4>Payable</h4>
                      {['cu', 'au', 'ag'].map(m => (
                        <label key={`pay-${m}`} className="stacked">
                          {m === 'cu' ? 'Cu' : m === 'au' ? 'Au' : 'Ag'}
                          <input
                            type="number"
                            step="0.001"
                            value={config.payable[m]}
                            onChange={(e) => updateConfig('payable', m, parseFloat(e.target.value) || 0)}
                          />
                        </label>
                      ))}
                      <h4>Recovery</h4>
                      {['cu', 'au', 'ag'].map(m => (
                        <label key={`rec-${m}`} className="stacked">
                          {m === 'cu' ? 'Cu' : m === 'au' ? 'Au' : 'Ag'}
                          <input
                            type="number"
                            step="0.001"
                            value={config.recovery[m]}
                            onChange={(e) => updateConfig('recovery', m, parseFloat(e.target.value) || 0)}
                          />
                        </label>
                      ))}
                      <h4>Price</h4>
                      <label className="stacked">
                        Cu $/lb
                        <input
                          type="number"
                          step="0.01"
                          value={config.prices.cuPerLb}
                          onChange={(e) => updateConfig('prices', 'cuPerLb', parseFloat(e.target.value) || 0)}
                        />
                      </label>
                      <label className="stacked">
                        Au $/oz
                        <input
                          type="number"
                          step="1"
                          value={config.prices.auPerOz}
                          onChange={(e) => updateConfig('prices', 'auPerOz', parseFloat(e.target.value) || 0)}
                        />
                      </label>
                      <label className="stacked">
                        Ag $/oz
                        <input
                          type="number"
                          step="0.1"
                          value={config.prices.agPerOz}
                          onChange={(e) => updateConfig('prices', 'agPerOz', parseFloat(e.target.value) || 0)}
                        />
                      </label>
                      <h4>Other</h4>
                      <label className="stacked">
                        CAD / USD
                        <input
                          type="number"
                          step="0.01"
                          value={config.cadUsd}
                          onChange={(e) => setConfig(p => ({ ...p, cadUsd: parseFloat(e.target.value) || 0 }))}
                        />
                      </label>
                      <label className="stacked">
                        Offsite $/t
                        <input
                          type="number"
                          step="0.1"
                          value={config.offsiteUsdPerTonne}
                          onChange={(e) => setConfig(p => ({ ...p, offsiteUsdPerTonne: parseFloat(e.target.value) || 0 }))}
                        />
                      </label>
                      <label className="stacked">
                        Royalty % NSR
                        <input
                          type="number"
                          step="0.001"
                          value={config.royaltyPctNsr}
                          onChange={(e) => setConfig(p => ({ ...p, royaltyPctNsr: parseFloat(e.target.value) || 0 }))}
                        />
                      </label>
                      <label className="stacked">
                        Oxide %
                        <input
                          type="number"
                          step="0.1"
                          value={config.oxideRatioPct}
                          onChange={(e) => setConfig(p => ({ ...p, oxideRatioPct: parseFloat(e.target.value) || 0 }))}
                        />
                      </label>
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column */}
              <div className="right-column">
                {/* Drill String View */}
                <div className="panel tall-panel">
                  <div className="panel-header sticky">
                    <IconText icon={ICON.uiFilter} as="h3">Drill String View</IconText>
                  </div>
                  <div className="panel-scroll">
                    {!holeRows.length || !range ? (
                      <p className="hint">Select a hole to view intervals. Shift+drag to extend selection.</p>
                    ) : (
                      <IntervalStrip
                        rows={viewMode === 'blockModel' ? holeRowsWithBlockModel : holeRows}
                        selection={{ from: fromDepth, to: toDepth }}
                        range={range}
                        pxPerMeter={pxPerMeter}
                        rockColors={rockColors}
                        barScale={barScale}
                        selectedIds={selectedIds}
                        economicConfig={config}
                        viewMode={viewMode}
                        onSelectionChange={handleSelectionChange}
                      />
                    )}
                  </div>
                </div>

                {/* Composite Table */}
                <div className="panel">
                  <div className="panel-header row">
                    <IconText icon={ICON.uiClipboardList} as="h3">Composite Table</IconText>
                    <div className="actions-row">
                      <Button 
                        size="small" 
                        variant="ghost" 
                        onClick={removeSelectedCompositeRows} 
                        disabled={!tableSelection.size}
                      >
                        <IconText icon={ICON.uiRemove} iconSize={16}>Remove selected</IconText>
                      </Button>
                      <Button 
                        size="small" 
                        onClick={exportCompositeRows} 
                        disabled={!compositeRows.length}
                      >
                        <IconText icon={ICON.uiExport} iconSize={16}>Export CSV</IconText>
                      </Button>
                    </div>
                  </div>
                  {!compositeRows.length ? (
                    <p className="hint">Add composites to build the table.</p>
                  ) : (
                    <div className="composite-table-wrapper">
                      <table className="composite-table">
                        <thead>
                          <tr>
                            <th></th>
                            <th>Hole ID</th>
                            <th>Incl.</th>
                            <th>From (m)</th>
                            <th>To (m)</th>
                            <th>Length (m)</th>
                            <th>Cu %</th>
                            <th>Au g/t</th>
                            <th>Ag g/t</th>
                            <th>CuEq %</th>
                            <th>Lithology</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...compositeRows].sort((a, b) => 
                            a.hole_id.localeCompare(b.hole_id) || a.from - b.from || a.to - b.to
                          ).map(row => (
                            <tr key={row.id} className={tableSelection.has(row.id) ? 'selected' : ''}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={tableSelection.has(row.id)}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setTableSelection(prev => {
                                      const next = new Set(prev);
                                      if (checked) next.add(row.id);
                                      else next.delete(row.id);
                                      return next;
                                    });
                                  }}
                                />
                              </td>
                              <td>{row.hole_id}</td>
                              <td>{row.incl ? 'Incl.' : ''}</td>
                              <td>{row.from.toFixed(2)}</td>
                              <td>{row.to.toFixed(2)}</td>
                              <td>{row.length.toFixed(2)}</td>
                              <td>{row.cu.toFixed(3)}</td>
                              <td>{row.au.toFixed(3)}</td>
                              <td>{row.ag.toFixed(2)}</td>
                              <td>{row.cuEq.toFixed(3)}</td>
                              <td>{row.lithology}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}

        {!loading && !error && 
         ((dataSource === 'database' && availableHoles.length === 0) || 
          (dataSource === 'csv' && assayData.length === 0)) && (
          <Card className="error-card">
            <IconText icon={ICON.uiEmptyMailbox} as="h3">No Data Available</IconText>
            <p>No assay data found in the database. Try uploading a CSV file to get started.</p>
            <label className="button">
              <IconText icon={ICON.uiFolderOpen} iconSize={16}>Load CSV File</IconText>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleCSVUpload(file);
                }}
                style={{ display: 'none' }}
              />
            </label>
          </Card>
        )}
      </div>

      {/* Hole Location Map Modal */}
      <HoleLocationModal
        open={showMapModal}
        onClose={() => setShowMapModal(false)}
        hole={selectedHoleMapData}
        allHoles={allHolesMapData}
      />
    </Layout>
  );
};

// Helper component for result items
function ResultItem({ label, value, isCuEq }) {
  return (
    <div className={`result-item ${isCuEq ? 'cueq-item' : ''}`}>
      <div className="result-label">{label}</div>
      <div className="result-value">{value ?? '—'}</div>
    </div>
  );
}

export default AssayCompositing;

